import { Table, TableColumnsType } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { createTestCodes, getClassTests, getTestStudents, openTestResults } from "../../../api/tutor.api";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { getCommonCodeLabel } from "../../../lib/store";
import { IClassTestInfo, IGetClassTests, IGetTestStudents, ITestCodeReq, ITestStudent } from "../../../models/tutor.model";
import Button from "../../Button";
import Checkbox from "../../Checkbox";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";

const Block = styled.div`
  /*.ant-table-wrapper {
    margin-top: -10px;
  }*/
`;

interface IProps {
  setTab: (tab: string) => void;
  testRecord: IClassTestInfo|undefined;
  setTestRecord: (testRecord: IClassTestInfo|undefined) => void;
  setStudentRecord: (studentRecord: ITestStudent) => void;
  classCode: string|undefined;
}

const ClassTest: FC<IProps> = ({
  setTab,
  testRecord,
  setTestRecord,
  setStudentRecord,
  classCode,
}) => {
  const { t, i18n } = useTranslation();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetClassTests>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetTestStudents>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();
  const { data: submitRes2, isLoading: submitIsLoading2, error: submitError2, execute: submitExecute2 } = useAxios();

  const [memberCodes, setMemberCodes] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  useEffect(() => {
    if (classCode) {
      execute(getClassTests(classCode));
    }
  }, [classCode]);

  useEffect(() => {
    if (testRecord) {
      doGetTestStudents();
    }
  }, [testRecord]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetTestStudents();
      successNotification(t('class.class.message32'));
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (submitRes2 && !submitIsLoading2) {
      doGetTestStudents();
      successNotification(t('common.button.processed'));
    }
  }, [submitRes2, submitIsLoading2]);

  const doGetTestStudents = () => {
    if (classCode && testRecord) {
      execute2(getTestStudents(classCode, testRecord.scheduleCode));
    }
  }

  const onSelect = (memberCode: string) => {
    if (memberCodes.includes(memberCode)) {
      const _memberCodes = memberCodes.filter(code => code !== memberCode);
      setMemberCodes(_memberCodes);
      setIsSelectAll(false);
    } else {
      const _memberCodes = [...memberCodes, memberCode];
      setMemberCodes(_memberCodes);
    }
  };

  const onSelectAll = () => {
    if (isSelectAll) {
      setMemberCodes([]);
      setIsSelectAll(false);
    } else {
      const _memberCodes: string[] = [];
      res2?.data?.forEach(item => {
        _memberCodes.push(item.memberCode);
      });
      setMemberCodes(_memberCodes);
      setIsSelectAll(true);
    }
  };

  const onGenerateCode = (memberCode: string) => {
    if (!classCode || !testRecord) return;
    const req: ITestCodeReq[] = [{
      examCode: testRecord.examCode,
      memberCode,
    }];
    submitExecute(createTestCodes(classCode, testRecord.scheduleCode, req));
  }

  const onGenerateCodes = () => {
    if (!classCode || !testRecord) return;
    const req: ITestCodeReq[] = [];
    res2?.data?.forEach(item => {
      if (!item.certifyKey && memberCodes.includes(item.memberCode)) {
        req.push({
          examCode: testRecord.examCode,
          memberCode: item.memberCode,
        });
      }
    });
    
    submitExecute(createTestCodes(classCode, testRecord.scheduleCode, req));
    setMemberCodes([]);
  }

  const onOpenResults = () => {
    const examTestCodes: number[] = [];
    res2?.data?.forEach(item => {
      if (item.examTestCode && memberCodes.includes(item.memberCode)) {
        examTestCodes.push(item.examTestCode);
      }
    });
    const req: any = examTestCodes.map(examTestCode => ({
      examTestCode,
      openYn: 'Y',
    }));
    submitExecute2(openTestResults(req));
    setMemberCodes([]);
  }

  const onToggleResult = (examTestCode: number, openYn: string) => {
    submitExecute2(openTestResults([{
      examTestCode,
      openYn: openYn === 'Y' ? 'N' : 'Y',
    }]));
    setMemberCodes([]);
  }

  const testColumns: TableColumnsType<IClassTestInfo> = [
    {
      title: t('sms.list.column2'),
      dataIndex: 'examDiv',
      align: 'center',
      render: value => getCommonCodeLabel('TE0001', value, 'ko'),
    },
    {
      title: t('class.class.column3'),
      dataIndex: 'scheduleName',
      align: 'center',
    },
    {
      title: t('class.class.column4'),
      dataIndex: 'srtDate',
      align: 'center',
      render: value => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('class.class.column5'),
      dataIndex: 'endDate',
      align: 'center',
      render: value => moment(value).format('YYYY.MM.DD'),
    },
  ];

  const studentColumns: TableColumnsType<ITestStudent> = [
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          onClickCapture={(e: any) => {
            e.stopPropagation();
            onSelectAll();
          }}
          text=""
        />
      ),
      dataIndex: 'memberCode',
      align: 'center',
      render: value => (
        <Checkbox
          checked={memberCodes.includes(value)}
          onChange={() => onSelect(value)}
          text=""
        />
      )
    },    
    {
      title: t('student.student.column5'),
      dataIndex: 'memKorName',
      align: 'center',
      render: (value, record) => i18n.language === 'ko' ? value : record.memEngName
    },
    {
      title: t('class.class.column6'),
      dataIndex: 'certifyKey',
      align: 'center',
      render: (value, record) => value || (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClick={() => onGenerateCode(record.memberCode)}
            text={t('class.class.button2')}
          />
        </div>
      ),
    },
    {
      title: t('class.class.column7'),
      dataIndex: 'completeYn',
      align: 'center',
      render: value => value || '-',
    },
    {
      title: t('board.notice.column7'),
      dataIndex: 'openYn',
      align: 'center',
      render: (value, record) => value
        ? (
          <>
            <div className="comm-btn__area justify-content-center">
              <span className="v-line">{value}</span>
              <Button
                className="btn-basic small mint"
                onClick={() => onToggleResult(record.examTestCode, value)}
                text={value === 'Y' ? t('common.text.private') : t('common.text.public')}
              />
            </div>
          </>
          )
        : '-'
    },
    {
      title: t('class.class.column97'),
      dataIndex: 'commentCount',
      align: 'center',
      render: (value, record) => (
        <>{t('class.class.text31')}{value}/{record.teacherCount}{t('class.class.text32')}</>
      ),
    },
    {
      title: t('class.class.column8'),
      dataIndex: 'examTestCode',
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClick={() => {
              setStudentRecord(record);
              setTab('testScore');
            }}
            text={t('class.class.button3')}
            disabled={!record.examTestCode}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading || isLoading2 || submitIsLoading || submitIsLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={submitError2} />

      <Table
        className='comm-table__area'
        columns={testColumns}
        dataSource={res?.data?.testInfo}
        pagination={{
          pageSize: 9999999
        }}
        onRow={(record) => {
          return {
            onClick: () => {
              setTestRecord({
                ...record,
                eduStepCode: res?.data?.eduStepCode,
              });
            },
          };
        }}
        rowClassName={(record: any) => record.scheduleCode === testRecord?.scheduleCode ? 'chosen' : 'pointer'}
      />

      {
        testRecord
          ? (
            <Block>
              <div className="comm-bx__space">
                <div className="comm-btn__area right">
                  <Button
                    className="btn-basic pink-line mr-1"
                    onClick={onGenerateCodes}
                    text={t('class.class.button1')}
                  />
                  <Button
                    className="btn-basic pink-line"
                    onClick={onOpenResults}
                    text={t('class.class.button14')}
                  />
                </div>
              </div>
              <Table
                className='comm-table__area'
                columns={studentColumns}
                dataSource={res2?.data}
                pagination={{
                  pageSize: 9999999
                }}
              />
            </Block>
            )
          : null
      }
    </>
  );
}

export default ClassTest;
